import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

const categories = ['All', 'Web Development', 'SEO', 'Digital Marketing', 'Startup Tips', 'Technology Trends'];

const mockPosts = [
  {
    id: '2',
    title: '10 SEO Strategies That Actually Work',
    slug: 'seo-strategies-2026',
    excerpt: 'Proven SEO tactics to boost your website\'s visibility and organic traffic.',
    content: 'Full article content...',
    author: 'Jane Smith',
    category: 'SEO',
    published_date: '2026-01-10',
    featured: true,
    image_url: 'https://images.unsplash.com/photo-1432888622747-4eb9a8f2c293?q=80&w=800',
  },
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [posts, setPosts] = useState(mockPosts);

  const filteredPosts = activeCategory === 'All'
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Blog - FuturePath Technologies</title>
        <meta name="description" content="Latest insights on web development, SEO, digital marketing, and technology trends." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="blog-hero-title">
              Our Blog
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Insights, tips, and industry news from our experts
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="blog-posts-section">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setActiveCategory(category)}
                variant={activeCategory === category ? 'default' : 'outline'}
                className={`rounded-full ${
                  activeCategory === category
                    ? 'bg-primary neon-glow'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`blog-post-${index}`}
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="glass-card rounded-3xl overflow-hidden hover:scale-105 transition-transform duration-500">
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <span className="text-primary font-mono">{post.category}</span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(post.published_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold font-outfit text-white mb-3 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User size={16} />
                        <span>{post.author}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
